import FilterListIcon from "@mui/icons-material/FilterList";
import GridViewIcon from "@mui/icons-material/GridView";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
  Avatar,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import api from "../../../redux/actions/api";
import { getDiagnosticDashboardData } from "../../../redux/reducers/clinicSlice";
import { getPetsCompliantSummary } from "../../../redux/reducers/petSlice";
import { AppColors } from "../../../util/AppColors";
import { diagnosticTypes, priorityList } from "../../../util/dropList";
import DiagnosticAddUpdate from "../../CommonScreens/Diagnostic/DiagnosticAddUpdate";
import DiagnosticReport from "../../CommonScreens/Diagnostic/DiagnosticReport";

const initFilter = {
  startDate: null,
  endDate: null,
  type: "all",
  priority: "all",
};

const getApiType = (filters = initFilter) =>
  filters?.type && filters.type !== "all" ? filters.type : "all";

const buildQueryParams = (search = "", filters = initFilter) => {
  let params = "";
  if (search?.length > 0) {
    params += `&search=${encodeURIComponent(search.trim())}`;
  }
  if (filters?.startDate) {
    params += `&startDate=${moment(new Date(filters.startDate)).format("YYYY-MM-DD")}`;
  }
  if (filters?.endDate) {
    params += `&endDate=${moment(new Date(filters.endDate)).format("YYYY-MM-DD")}`;
  }
  if (filters?.priority && filters.priority !== "all") {
    params += `&priority=${encodeURIComponent(filters.priority)}`;
  }
  return params;
};

const STATUS_API_PARAMS = {
  todo: ["scheduled"],
  in_progress: ["in progress"],
  pending_results: ["patient review", "pending review"],
  completed: ["completed"],
};

const buildStatusParams = (statusTab) =>
  (STATUS_API_PARAMS[statusTab] ?? [])
    .map((status) => `&status=${encodeURIComponent(status)}`)
    .join("");

const STATUS_TAB_CONFIG = [
  { label: "To do", value: "todo" },
  { label: "In progress", value: "in_progress" },
  { label: "Pending review", value: "pending_results" },
  { label: "Completed", value: "completed" },
];

const tblHdrs = [
  "type",
  "time",
  "case",
  "patient",
  "customerName",
  "test",
  "status",
  "actions",
];

const DIAGNOSTIC_STATUS_BY_TAB = STATUS_API_PARAMS;

const normalizeDiagnosticStatus = (status = "") =>
  status
    ?.toString()
    ?.trim()
    ?.toLowerCase()
    ?.replace(/_/g, " ")
    ?.replace(/\s+/g, " ");

const getDiagnosticStatus = (item) =>
  item?.diagnostic?.status ?? item?.status ?? "";

const getDiagnosticStatusTab = (status) => {
  const normalized = normalizeDiagnosticStatus(status);
  if (!normalized) return null;

  return (
    Object.entries(DIAGNOSTIC_STATUS_BY_TAB).find(([, statuses]) =>
      statuses.includes(normalized),
    )?.[0] ?? null
  );
};

const toDiagnosticArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const getDashboardRecords = (dashboard) => {
  if (!dashboard) return [];
  if (Array.isArray(dashboard?.records)) return dashboard.records;
  if (Array.isArray(dashboard)) return dashboard;
  return [];
};

const flattenDiagnosticList = (records = []) =>
  records?.flatMap((item) => {
    const diagnostics = toDiagnosticArray(item?.diagnostic);
    const resolvedDiagnostics = diagnostics.length
      ? diagnostics
      : toDiagnosticArray(item?.diagnostics);

    const list =
      resolvedDiagnostics.length > 0
        ? resolvedDiagnostics
        : item?.diagonosticId || item?.testName || item?.status
          ? [item]
          : [];

    return list.map((diag) => ({
      diagnostic: diag?.diagnostic ? diag.diagnostic : diag,
      appoinment: item?.appointment ?? item?.appoinment ?? diag?.appointment,
      paymentId: item?.paymentId,
    }));
  });

const getStatusBadgeType = (status) => {
  const normalized = normalizeDiagnosticStatus(status);
  if (normalized === "scheduled") return "scheduled";
  if (normalized === "in progress") return "in_progress";
  if (["patient review", "pending review"].includes(normalized)) {
    return "pending_results";
  }
  if (normalized === "completed") return "reported";
  return "routine";
};

const getPrimaryAction = (tab, type) => {
  if (tab === "todo") {
    return "Study Performed";
    // return (type ?? "").toLowerCase().includes("lab")
    //   ? "Collect Sample"
    //   : "Take Test";
  }
  if (tab === "in_progress") return "Enter results";
  if (tab === "pending_results") return "Modify results";
  return null;
};

const getTablePrimaryAction = (tab, isLab) => {
  if (tab === "todo") return isLab ? "Collect" : "Start";
  if (tab === "in_progress") return "Enter results";
  if (tab === "pending_results") return "Modify";
  return null;
};

const getDetailPrefix = (tab, isLab) => {
  if (tab === "todo") return isLab ? "Tests:" : "Study:";
  if (tab === "in_progress") return "In progress:";
  if (tab === "pending_results") return "Pending:";
  if (tab === "completed") return "Reported:";
  return "Tests:";
};

const mapDiagnosticToCaseData = (item) => {
  const diag = item?.diagnostic ?? {};
  const apnt = item?.diagnostic?.appoinment ?? {};
  const tab = getDiagnosticStatusTab(diag?.status);
  const type = diag?.type ?? apnt?.serviceType ?? "";
  const isLab = type?.toLowerCase()?.includes("lab");
  const petName = apnt?.petName ?? "";
  const genderLabel = apnt?.gender
    ? ` (${apnt.gender.charAt(0).toUpperCase()}${apnt.gender.slice(1)})`
    : "";
  const createdDate = diag?.createdDate
    ? moment(new Date(diag.createdDate))
    : null;
  const caseId = apnt?.caseId ?? apnt?.appointmentId ?? diag?.diagonosticId;
  const services = apnt?.serviceNames
    ?.flat(Infinity)
    ?.map((service) => service?.serviceName)
    ?.filter(Boolean)
    ?.join(", ");
  const detail = diag?.testName || services || apnt?.remarks || "-";

  return {
    id: `${apnt?.appointmentId ?? "na"}-${diag?.diagonosticId ?? ""}`,
    category: isLab ? "LAB" : type?.toUpperCase() || "RADIOLOGY",
    time: apnt?.appointmentdateTime
      ? moment(new Date(apnt?.appointmentdateTime)).format("DD MMM YYYY")
      : "-",
    caseLabel: createdDate
      ? `${createdDate.calendar(null, {
          sameDay: "[Today]",
          lastDay: "[Yesterday]",
          lastWeek: "DD MMM YYYY",
          sameElse: "DD MMM YYYY",
        })} · Case ${caseId ?? "-"}`
      : `Case ${caseId ?? "-"}`,
    priority: "Routine",
    priorityType: "routine",
    petName: `${petName}${genderLabel}`,
    breed: [apnt?.breed, apnt?.weight ? `${apnt.weight} kg` : null]
      .filter(Boolean)
      .join(" · "),
    petInitial: petName?.[0]?.toUpperCase() ?? "?",
    caseNumber: caseId ?? "-",
    patientDisplay: petName
      ? `${petName}${apnt?.breed ? ` (${apnt.breed})` : ""}`
      : "-",
    testsStudy: detail,
    isLab,
    tablePrimaryAction: getTablePrimaryAction(tab, isLab),
    station: diag?.station || apnt?.station || type || "-",
    petParent: apnt?.userName ?? "-",
    detailPrefix: getDetailPrefix(tab, isLab),
    detail,
    requestedBy: apnt?.doctorName ? `Dr. ${apnt.doctorName}` : "-",
    statusBadge: diag?.status ?? "-",
    statusBadgeType: getStatusBadgeType(diag?.status),
    primaryAction: getPrimaryAction(tab, type),
    primaryColor: isLab ? "#0054A6" : "#7B2D8E",
    showPrint: tab === "completed",
    quantity: diag?.quantity,
    diag: { ...diag, paymentId: item?.paymentId ?? null },
    apnt,
  };
};

const BADGE_STYLES = {
  routine: { bg: "#101316", color: "#828282" },
  stat: { bg: "#D32F2F", color: "#fff" },
  urgent: { bg: "#FFEBEE", color: "#D32F2F" },
  scheduled: { bg: "#FFF8E1", color: "#E65100" },
  in_progress: { bg: "#E3F2FD", color: "#0054A6" },
  pending_results: { bg: "#F3E5F5", color: "#7B1FA2" },
  results_ready: { bg: "#E8F5E9", color: "#2E7D32" },
  reported: { bg: "#E8EEF5", color: "#5C6B7A" },
};

const segmentedToggleSx = {
  backgroundColor: "#fff",
  borderRadius: "6px",
  border: "1px solid #f0f2f5",
  "& .MuiToggleButtonGroup-grouped": {
    borderRadius: "5px !important",
    border: "1px solid transparent",
    color: "#6b7280",
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    padding: "4px 16px",
    fontFamily: "Montserrat",
    "&.Mui-selected": {
      backgroundColor: AppColors.appPrimary,
      color: AppColors.white,
      "&:hover": {
        backgroundColor: AppColors.appPrimary,
      },
    },
  },
};

const iconToggleSx = {
  backgroundColor: "#f0f2f5",
  borderRadius: "6px",
  "& .MuiToggleButtonGroup-grouped": {
    borderRadius: "5px !important",
    border: "1px solid transparent",
    color: AppColors.gray3,
    padding: "4px 8px",
    minWidth: 32,
    "&.Mui-selected": {
      backgroundColor: AppColors.appPrimary,
      color: AppColors.white,
      "&:hover": {
        backgroundColor: AppColors.appPrimary,
      },
    },
  },
};

const PillBadge = ({ label, type }) => {
  const style = BADGE_STYLES[type] || BADGE_STYLES.routine;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        backgroundColor: style.bg,
        color: style.color,
        borderRadius: "4px",
        px: 1,
        py: 0.25,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "Montserrat",
        lineHeight: 1.4,
      }}
    >
      {label}
    </Box>
  );
};

const StatusChip = ({ label, count, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      padding: "6px 14px",
      borderRadius: "5px",
      border: active ? "none" : `1px solid ${AppColors.btnGrayBorder}`,
      backgroundColor: active ? AppColors.appPrimary : AppColors.white,
      color: active ? AppColors.white : AppColors.gray3,
      fontWeight: 600,
      fontSize: 14,
      fontFamily: "Montserrat",
      whiteSpace: "nowrap",
    }}
  >
    {label}
    {/* {count != null && (
      <Typography
        component="span"
        sx={{ fontSize: 12, fontWeight: 600, opacity: active ? 0.9 : 1 }}
      >
        {count}
      </Typography>
    )} */}
  </Box>
);

const CriticalAlert = () => (
  <div className="critical mt15">
    <div className="flex-row-ali-cen">
      <ReportProblemOutlinedIcon className="red8 fs16" />
      <Typography className="red8 fs14 font-bold ml5">Critical:</Typography>
    </div>
    <Typography className="red8 fs14">
      Rex (Case 825) – Creatinine critical.{" "}
    </Typography>
    <Typography className="underline blue-color fs14 txt-semi-bold">
      Review & report
    </Typography>
  </div>
);

const LabDashboardNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("allCases");
  const [timeFilter, setTimeFilter] = useState("today");
  const [layoutView, setLayoutView] = useState("grid");
  const [statusFilter, setStatusFilter] = useState("todo");
  const [searchValue, setSearchValue] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterValues, setFilterValues] = useState(initFilter);
  const [appliedFilters, setAppliedFilters] = useState(initFilter);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modVisible, setModVisible] = useState(false);
  const [reportDownVsble, setReportDownVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [gridPage, setGridPage] = useState(1);
  const [dashboardRecords, setDashboardRecords] = useState([]);
  const [gridTotalRecords, setGridTotalRecords] = useState(0);
  const { diagnosticDashboard } = useSelector((state) => state?.clinic);
  const profile = JSON.parse(localStorage.getItem("user"));
  const listInnerRef = useRef(null);
  const hasReachedBottomRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const skipSearchEffectRef = useRef(true);
  const gridItemSize = 9;
  const tableItemSize = 10;
  const reqId = profile?.id;

  const gridDiagnosticList = useMemo(
    () => flattenDiagnosticList(dashboardRecords),
    [dashboardRecords],
  );

  const tableDiagnosticList = useMemo(
    () => flattenDiagnosticList(getDashboardRecords(diagnosticDashboard)),
    [diagnosticDashboard],
  );

  const activeTotalRecords =
    layoutView === "grid"
      ? gridTotalRecords
      : (diagnosticDashboard?.totalRecords ?? 0);

  const statusTabs = useMemo(() => {
    return STATUS_TAB_CONFIG.map((tab) => ({
      ...tab,
      count: tab.value === statusFilter ? activeTotalRecords : null,
    }));
  }, [statusFilter, activeTotalRecords]);

  const gridCases = useMemo(
    () => gridDiagnosticList.map(mapDiagnosticToCaseData),
    [gridDiagnosticList],
  );

  const tableCases = useMemo(
    () => tableDiagnosticList.map(mapDiagnosticToCaseData),
    [tableDiagnosticList],
  );

  useEffect(() => {
    hasReachedBottomRef.current = false;
    setGridPage(1);
    if (layoutView !== "list") {
      onGridApiCall(1);
    }
    if (layoutView === "list") {
      onTableApiCall(1);
    }
  }, [timeFilter, statusFilter]);

  useEffect(() => {
    if (layoutView === "list") {
      onTableApiCall(1);
    }
  }, [layoutView]);

  const onTableApiCall = (
    pge = 1,
    filters = appliedFilters,
    statusTab = statusFilter,
  ) => {
    setPage(pge);
    const filterParams = buildQueryParams(searchValue, filters);
    const timeFilterParam =
      filters?.startDate || filters?.endDate ? "" : `&filter=${timeFilter}`;
    const statusParams = buildStatusParams(statusTab);
    const url = `&doctorId=${reqId}${timeFilterParam}&page=${pge}&itemSize=${tableItemSize}${filterParams}${statusParams}`;
    dispatch(
      getDiagnosticDashboardData({
        type: getApiType(filters),
        url,
      }),
    );
  };

  const onGridApiCall = async (
    pge = 1,
    append = false,
    filters = appliedFilters,
    statusTab = statusFilter,
  ) => {
    setGridPage(pge);
    const filterParams = buildQueryParams(searchValue, filters);
    const timeFilterParam =
      filters?.startDate || filters?.endDate ? "" : `&filter=${timeFilter}`;
    const statusParams = buildStatusParams(statusTab);
    const url = `&doctorId=${reqId}${timeFilterParam}&page=${pge}&itemSize=${gridItemSize}${filterParams}${statusParams}`;
    if (!append) {
      setDashboardRecords([]);
    }
    try {
      const res = await api({ contentType: true, auth: true }).get(
        `/appointments/clinic/doctors/radiology?type=${getApiType(filters)}${url}`,
      );
      if (res?.status !== 200) return;
      const payload = res?.data?.data;
      const newRecords = getDashboardRecords(payload);
      setGridTotalRecords(payload?.totalRecords ?? 0);
      setDashboardRecords((prev) =>
        append && pge > 1 ? [...prev, ...newRecords] : newRecords,
      );
    } catch {
      // Grid load-more errors are non-blocking; table data stays unchanged.
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (skipSearchEffectRef.current) {
        skipSearchEffectRef.current = false;
        return;
      }
      hasReachedBottomRef.current = false;
      setGridPage(1);
      if (layoutView !== "list") {
        onGridApiCall(1);
      }
      if (layoutView === "list") {
        onTableApiCall(1);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const onScroll = () => {
    if (layoutView !== "grid" || isLoadingMoreRef.current) return;

    const container = listInnerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 10;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    const isAtBottom = distanceFromBottom <= threshold;
    const hasMore = dashboardRecords.length < gridTotalRecords;

    if (isAtBottom && !hasReachedBottomRef.current && hasMore) {
      hasReachedBottomRef.current = true;
      isLoadingMoreRef.current = true;
      const nextPage = gridPage + 1;
      onGridApiCall(nextPage, true).finally(() => {
        isLoadingMoreRef.current = false;
      });
    } else if (!isAtBottom && hasReachedBottomRef.current) {
      hasReachedBottomRef.current = false;
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterValues({ ...filterValues, [name]: value });
  };

  const handleFilterReset = () => {
    setFilterValues(initFilter);
    setAppliedFilters(initFilter);
    setFilterOpen(false);
    hasReachedBottomRef.current = false;
    setGridPage(1);
    if (layoutView !== "list") {
      onGridApiCall(1, false, initFilter);
    }
    if (layoutView === "list") {
      onTableApiCall(1, initFilter);
    }
  };

  const handleFilterApply = () => {
    const nextFilters = { ...filterValues };
    setAppliedFilters(nextFilters);
    setFilterOpen(false);
    hasReachedBottomRef.current = false;
    setGridPage(1);
    if (layoutView !== "list") {
      onGridApiCall(1, false, nextFilters);
    }
    if (layoutView === "list") {
      onTableApiCall(1, nextFilters);
    }
  };

  const onCollectSample = (dia) => {
    setSelectedItem(dia?.diag);
    setModVisible(true);
  };

  const onCloseDownMod = () => {
    setReportDownVsble(false);
    setSelectedItem(null);
  };

  const onPrintReport = (itm) => {
    setSelectedItem(itm?.diag);
    setReportDownVsble(true);
  };

  const onViewCase = (itm) => {
    // if (itm?.apnt?.appointmentId) {
    //   dispatch(getPetsCompliantSummary(itm?.apnt?.appointmentId));
    // }
    navigate("/lab-pet-details", {
      state: {
        appointment: {
          appoinment: {
            petId: itm?.apnt?.petId,
            appointmentId: itm?.apnt?.appointmentId,
            userId: itm?.apnt?.userId,
          },
        },
      },
    });
  };

  const getTableCases = () =>
    tableCases?.map((caseData) => {
      return {
        type: (
          <Typography className="fs12 font-bold">
            {caseData.category}
          </Typography>
        ),
        time: caseData.time,
        case: caseData.caseNumber,
        patient: caseData.patientDisplay,
        customerName: caseData.petParent,
        test: caseData.testsStudy,
        status: (
          <PillBadge
            label={caseData.statusBadge}
            type={caseData.statusBadgeType}
          />
        ),
        actions: (
          <Box display="flex" alignItems="center" gap={1}>
            {caseData.primaryAction ? (
              <CustomButton
                smallBtn
                text={caseData.primaryAction}
                onClick={() => onCollectSample(caseData)}
              />
            ) : null}
            <CustomButton
              text="View case"
              smallBtn
              whiteBtn
              onClick={() => onViewCase(caseData)}
            />
            {caseData.showPrint ? (
              <CustomButton
                text="Print Report"
                smallBtn
                onClick={() => onPrintReport(caseData)}
              />
            ) : null}
          </Box>
        ),
      };
    }) ?? [];

  const LabCaseCard = ({ caseData, layoutView }) => {
    const priorityStyle =
      BADGE_STYLES[caseData.priorityType] || BADGE_STYLES.routine;

    return (
      <Box
        sx={{
          backgroundColor: AppColors.white,
          borderRadius: "8px",
          border: `1px solid ${AppColors.btnGrayBorder}`,
          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          height: 350,
          ...(layoutView === "list" && {
            flexDirection: "row",
            flexWrap: "wrap",
          }),
        }}
      >
        <div>
          <div>
            <div className="flex-row-between">
              <Typography
                className={`fs10 uppercase font-bold ${caseData?.category?.toLowerCase().includes("lab") ? "blue-color" : "purple"}`}
              >
                {caseData.category}
              </Typography>
              <div className="txt-semi-bold fs10 ph10 pv2 gray-back2 con-fit br5">
                {caseData.priority}
              </div>
            </div>
            <Typography className="fs18 font-bold">{caseData.time}</Typography>
            <Typography className="clr-muted fs12">
              {caseData.caseLabel}
            </Typography>
          </div>
        </div>

        <Box display="flex" alignItems="center" gap={1.5} width="100%">
          <Avatar variant="rounded" className="h40img">
            {caseData.petInitial}
          </Avatar>
          <Box>
            <Typography className="fs14 font-bold">
              {caseData.petName}
            </Typography>
            <Typography className="fs12 clr-muted">{caseData.breed}</Typography>
          </Box>
        </Box>

        <Box width="100%">
          <Typography className="fs12 clr-muted">
            Pet parent: {caseData.petParent}
          </Typography>
          {/* <PillBadge label={`Station: ${caseData.station}`} type="routine" /> */}
          <div className="flex-row-ali-cen flex-wrap mv3">
            <Typography className="fs14 font-bold">
              {caseData.detailPrefix}
            </Typography>
            <Typography className="fs12 ml5 clr-muted">
              {caseData.detail}
            </Typography>
          </div>

          <Typography className="fs12 clr-muted mv3">
            Requested by {caseData.requestedBy}
          </Typography>
          <Typography className="fs12 clr-muted mv3">
            Billable Qty: {caseData.quantity}
          </Typography>
          <PillBadge
            label={caseData.statusBadge}
            type={caseData.statusBadgeType}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={1} mt={0.5} width="100%">
          {caseData.primaryAction ? (
            <div>
              <CustomButton
                smallBtn
                text={caseData.primaryAction}
                onClick={() => onCollectSample(caseData)}
              />
            </div>
          ) : null}
          <div>
            <CustomButton
              text="View case"
              smallBtn
              whiteBtn
              onClick={() => onViewCase(caseData)}
            />
          </div>
          {caseData.showPrint && (
            <div>
              <CustomButton
                text="Print Report"
                smallBtn
                onClick={() => onPrintReport(caseData)}
              />
            </div>
          )}
        </Box>
      </Box>
    );
  };

  const handleModClose = (typ = null) => {
    if (typ === "apiSuccess") {
      if (layoutView === "grid") {
        setGridPage(1);
        onGridApiCall(1);
      } else {
        onTableApiCall(page);
      }
    }
    setModVisible(false);
    setSelectedItem(null);
  };

  const handleChangePage = (e, selectedPage) => {
    onTableApiCall(selectedPage);
  };

  return (
    <>
      <Box className="pv20 ph15 mh20">
        <Box className="white-color br8 ph15 pv10 box-shadow2">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1.5}
            mb={1.5}
            sx={{ minHeight: 40 }}
          >
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, val) => val && setViewMode(val)}
              size="small"
              sx={segmentedToggleSx}
            >
              <ToggleButton value="allCases">All Cases</ToggleButton>
              {/* <ToggleButton value="stationView">Station View</ToggleButton> */}
            </ToggleButtonGroup>

            <Box
              display="flex"
              alignItems="center"
              gap={1}
              flexWrap="nowrap"
              sx={{ minHeight: 40, flexShrink: 0 }}
            >
              <ToggleButtonGroup
                value={timeFilter}
                exclusive
                onChange={(_, val) => {
                  if (val) {
                    const nextFilters = {
                      ...appliedFilters,
                      startDate: null,
                      endDate: null,
                    };
                    setFilterValues((prev) => ({
                      ...prev,
                      startDate: null,
                      endDate: null,
                    }));
                    setAppliedFilters(nextFilters);
                    setTimeFilter(val);
                  }
                }}
                size="small"
                sx={segmentedToggleSx}
              >
                <ToggleButton value="today">Today</ToggleButton>
                <ToggleButton value="week">Week</ToggleButton>
              </ToggleButtonGroup>

              <Box
                onClick={() => setFilterOpen(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  padding: "4px 12px",
                  border: `1px solid ${filterOpen ? AppColors.appPrimary : AppColors.btnGrayBorder}`,
                  borderRadius: "5px",
                  cursor: "pointer",
                  color: filterOpen ? AppColors.appPrimary : AppColors.gray3,
                  backgroundColor: filterOpen ? "#f5f9ff" : AppColors.white,
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: "Montserrat",
                  height: 30,
                  flexShrink: 0,
                }}
              >
                <FilterListIcon sx={{ fontSize: 16 }} />
                Filter
              </Box>

              <Box
                sx={{
                  width: 200,
                  overflow: "hidden",
                  opacity: 1,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  pointerEvents: "auto",
                }}
              >
                <CustomTextField
                  fullWidth
                  placeholder="Search..."
                  value={searchValue}
                  handleChange={(e) => setSearchValue(e?.target?.value)}
                  backgroundColor={AppColors.white}
                />
              </Box>

              <ToggleButtonGroup
                value={layoutView}
                exclusive
                onChange={(_, val) => val && setLayoutView(val)}
                size="small"
                sx={iconToggleSx}
              >
                <ToggleButton value="grid">
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {statusTabs.map((tab) => (
              <StatusChip
                key={tab.value}
                label={tab.label}
                count={tab.count}
                active={statusFilter === tab.value}
                onClick={() => setStatusFilter(tab.value)}
              />
            ))}
          </Box>
        </Box>

        {/* <CriticalAlert /> */}

        {layoutView === "grid" ? (
          <Box
            ref={listInnerRef}
            onScroll={onScroll}
            className="scroll-70vh"
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: gridCases?.length
                ? "repeat(auto-fill, minmax(340px, 1fr))"
                : "1fr",
              gap: 2,
            }}
          >
            {gridCases?.length > 0 ? (
              gridCases?.map((caseData) => (
                <LabCaseCard
                  key={caseData.id}
                  caseData={caseData}
                  layoutView={layoutView}
                />
              ))
            ) : (
              <div className="flex-center w100Per">
                <div className="no-rec">No records available</div>
              </div>
            )}
          </Box>
        ) : (
          <CustomTable
            grey
            columns={tblHdrs}
            datas={getTableCases()}
            totalRecords={diagnosticDashboard?.totalRecords ?? 0}
            rowsPerPage={tableItemSize}
            page={page}
            handleChangePage={handleChangePage}
            maxHeight={450}
          />
        )}

        <CustomModal
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          header="Filter"
          rightModal
          modalWidth={30}
        >
          <div className="ph20">
            <div className="flex-row-between mt10">
              <div className="w48Per">
                <CustomTextField
                  fullWidth
                  label="Start Date"
                  handleChange={(e) =>
                    handleFilterChange("startDate", e?.target?.value)
                  }
                  value={filterValues?.startDate}
                  mobileDate
                />
              </div>
              <div className="w48Per">
                <CustomTextField
                  fullWidth
                  label="End Date"
                  handleChange={(e) =>
                    handleFilterChange("endDate", e?.target?.value)
                  }
                  value={filterValues?.endDate}
                  mobileDate
                />
              </div>
            </div>
            <div className="mt20">
              <CustomSelect
                label="Type"
                list={[{ name: "All", value: "all" }, ...diagnosticTypes]}
                value={filterValues?.type}
                handleChange={(e) =>
                  handleFilterChange("type", e?.target?.value)
                }
                select
              />
            </div>
            <div className="mt20">
              <CustomSelect
                label="Priority"
                list={[{ name: "All", value: "all" }, ...priorityList]}
                value={filterValues?.priority}
                handleChange={(e) =>
                  handleFilterChange("priority", e?.target?.value)
                }
                select
              />
            </div>
            <div className="flex-row-center mt15 mb10">
              <div>
                <CustomButton
                  text="Reset"
                  smallBtn
                  redBtn
                  onClick={handleFilterReset}
                />
              </div>
              <div className="ml20">
                <CustomButton
                  text="Apply"
                  smallBtn
                  onClick={handleFilterApply}
                />
              </div>
            </div>
          </div>
        </CustomModal>
      </Box>

      {modVisible ? (
        <DiagnosticAddUpdate
          modVisible={modVisible}
          handleModClose={handleModClose}
          selectedItem={selectedItem}
        />
      ) : null}
      {reportDownVsble ? (
        <DiagnosticReport
          reportDownVsble={reportDownVsble}
          onCloseDownMod={onCloseDownMod}
          selectedItem={selectedItem}
        />
      ) : null}
    </>
  );
};

export default LabDashboardNew;
